import Link from 'next/link'
import { MC, SERIF } from '@/lib/mycio/theme'
import type { CoverageTier } from '@/components/mycio/marketing/landing/content'
import type { CoverageT } from '@/lib/mycio/coverageI18n'

/** Shared bits for the coverage pages — same tokens as the landing (eyebrow, serif H1/H2, cards, band pills). */
export const EYEBROW: React.CSSProperties = { fontSize: 12.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 18px' }
export const H1: React.CSSProperties = { fontFamily: SERIF, fontWeight: 400, fontSize: 56, lineHeight: 1.05, letterSpacing: '-0.025em', margin: '0 0 18px', color: MC.ink, textWrap: 'balance' }
export const H2: React.CSSProperties = { fontFamily: SERIF, fontWeight: 400, fontSize: 34, lineHeight: 1.12, letterSpacing: '-0.02em', margin: '0 0 18px', color: MC.ink, textWrap: 'balance' }
export const H3: React.CSSProperties = { fontFamily: SERIF, fontWeight: 500, fontSize: 22, margin: '0 0 8px', color: MC.ink }
export const LEAD: React.CSSProperties = { fontSize: 18, lineHeight: 1.6, color: MC.muted, margin: '0 0 28px', maxWidth: 680, textWrap: 'pretty' }
export const BODY: React.CSSProperties = { fontSize: 16, lineHeight: 1.6, color: MC.muted, margin: 0, textWrap: 'pretty' }
export const CARD: React.CSSProperties = { background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 14, padding: '22px 24px' }
export const LABEL: React.CSSProperties = { fontSize: 11.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 8px' }

/** English reference values; the views translate through `t` (coverageI18n). Kept for any non-view consumer. */
export const TIER_LABEL: Record<CoverageTier, string> = { validated: 'Validated', provisional: 'Provisional' }
export const TIER_NOTE: Record<CoverageTier, string> = {
  validated: 'validated by our own worked cases',
  provisional: 'modelled, some rules still being verified',
}

export function TierPill({ tier, size = 12, t }: { tier: CoverageTier; size?: number; t?: CoverageT }) {
  const validated = tier === 'validated'
  const label = t ? t.ui(validated ? 'validated' : 'provisional') : TIER_LABEL[tier]
  return (
    <span style={{ display: 'inline-block', fontSize: size, fontWeight: 600, letterSpacing: '0.02em', color: validated ? MC.forest : MC.clay, background: validated ? MC.sageBg : MC.clayBg, padding: '4px 11px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  )
}

/** The same estimate / not-advice footer the landing's coverage section carries. */
export function EstimateFooter({ t }: { t?: CoverageT }) {
  const body = t ? t.ui('estimateFooter') : 'Figures are estimates for planning — computed, not advice. They are not tax-filing grade and not a substitute for a licensed adviser. Every country page lists what is encoded and what is not.'
  const link = t ? t.ui('readDisclosures') : 'Read the disclosures'
  return (
    <div style={{ ...CARD, background: '#EEE8DC', marginTop: 40 }}>
      <p style={{ ...BODY, fontSize: 15, color: MC.inkSoft }}>
        {body}{' '}
        <Link href="/#disclosures" style={{ color: MC.forest }}>{link}</Link>.
      </p>
    </div>
  )
}

export function Flag({ slug }: { slug: string }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={`https://flagcdn.com/w80/${slug}.png`} alt="" loading="lazy" style={{ width: 28, height: 'auto', borderRadius: 3, display: 'block', boxShadow: '0 0 0 1px rgba(35,33,28,0.06)' }} />
}
